import React, { useRef, useState, useEffect } from 'react';
import { Vector3, Mesh, Plane as ThreePlane, Vector2 } from 'three';
import { useGameStore } from '../store/gameStore';
import TowerPreview from '../components/TowerPreview';
import { useThree } from '@react-three/fiber';
import { useResponsive } from '../hooks/useResponsive';

interface SchoolMapProps {
  onClick: (position: Vector3) => void;
  onHover: (position: Vector3) => void;
}

const SchoolMap: React.FC<SchoolMapProps> = ({ onClick, onHover }) => {
  const { path, currentLevel, selectedTowerType, setTowerPreviewPosition, towerPreviewPosition } = useGameStore();
  const groundRef = useRef<Mesh>(null);
  const [cursorPosition, setCursorPosition] = useState<Vector3 | null>(null);
  const { camera, raycaster } = useThree();
  const { isMobile, isTablet } = useResponsive();
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  // Handle touch interactions
  useEffect(() => {
    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault(); // Prevent default touch behavior
      
      if (touchStartRef.current) {
        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - touchStartRef.current.x;
        const deltaY = touch.clientY - touchStartRef.current.y;
        const deltaTime = Date.now() - touchStartRef.current.time;

        // If it's a tap (small movement and quick)
        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10 && deltaTime < 200) {
          const groundPlane = new ThreePlane(new Vector3(0, 1, 0), 0);
          const intersection = new Vector3();
          
          // Update raycaster with touch coordinates
          raycaster.setFromCamera(
            new Vector2(touch.clientX, touch.clientY),
            camera
          );
          
          raycaster.ray.intersectPlane(groundPlane, intersection);
          
          if (intersection && groundRef.current && selectedTowerType) {
            const position = intersection.clone();
            position.y = 0;
            
            // Round to grid with 0.5 unit precision
            position.x = Math.round(position.x * 2) / 2;
            position.z = Math.round(position.z * 2) / 2;
            
            if (!isOnPath(position) && !isTooCloseToTowers(position)) {
              onClick(position);
            }
          }
        }
      }
      
      touchStartRef.current = null;
    };

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault(); // Prevent default touch behavior
      
      if (e.touches.length === 1) {
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          time: Date.now()
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault(); // Prevent default touch behavior
      
      if (e.touches.length === 1 && groundRef.current) {
        const touch = e.touches[0];
        const groundPlane = new ThreePlane(new Vector3(0, 1, 0), 0);
        const intersection = new Vector3();
        
        // Update raycaster with touch coordinates
        raycaster.setFromCamera(
          new Vector2(touch.clientX, touch.clientY),
          camera
        );
        
        raycaster.ray.intersectPlane(groundPlane, intersection);
        
        if (intersection) {
          const position = intersection.clone();
          position.y = 0;
          
          // Round to grid with 0.5 unit precision
          position.x = Math.round(position.x * 2) / 2;
          position.z = Math.round(position.z * 2) / 2;
          
          setCursorPosition(position);
          
          if (!isOnPath(position) && !isTooCloseToTowers(position) && selectedTowerType) {
            setTowerPreviewPosition(position);
          } else {
            setTowerPreviewPosition(null);
          }
          
          onHover(position);
        }
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [raycaster, selectedTowerType, onClick, camera, onHover]);

  // Handle mouse click on the ground
  const handleClick = (e: any) => {
    e.stopPropagation();
    
    if (groundRef.current && selectedTowerType) {
      // Get the clicked position on the ground
      const position = e.point.clone();
      position.y = 0; // Ensure y is 0 (ground level)
      
      // Round to grid with 0.5 unit precision for more flexible placement
      position.x = Math.round(position.x * 2) / 2;
      position.z = Math.round(position.z * 2) / 2;
      
      // Only allow placement if not on path and not too close to other towers
      if (!isOnPath(position) && !isTooCloseToTowers(position)) {
        onClick(position);
      }
    }
  };

  // Handle mouse move over the ground
  const handlePointerMove = (e: any) => {
    e.stopPropagation();
    
    if (groundRef.current) {
      // Get the hovered position on the ground
      const position = e.point.clone();
      position.y = 0; // Ensure y is 0 (ground level)
      
      // Round to grid with 0.5 unit precision
      position.x = Math.round(position.x * 2) / 2;
      position.z = Math.round(position.z * 2) / 2;
      
      // Update cursor position
      setCursorPosition(position);
      
      // Only show preview if not on path, not too close to towers, and a tower is selected
      if (!isOnPath(position) && !isTooCloseToTowers(position) && selectedTowerType) {
        setTowerPreviewPosition(position);
      } else {
        setTowerPreviewPosition(null);
      }
      
      onHover(position);
    }
  };

  // Check if a point is on or near the path
  const isOnPath = (position: Vector3): boolean => {
    return path.some(pathPoint => pathPoint.distanceTo(position) < 1.5);
  };

  // Check if a position is too close to existing towers
  const isTooCloseToTowers = (position: Vector3): boolean => {
    const { towers } = useGameStore.getState();
    return towers.some(tower => {
      const towerPos = new Vector3(tower.position.x, 0, tower.position.z);
      return towerPos.distanceTo(position) < 3;
    });
  };

  return (
    <group>
      {/* Ground */}
      <mesh 
        ref={groundRef}
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, 0, 0]}
        onClick={handleClick}
        onPointerMove={handlePointerMove}
      >
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial 
          color="#4a4a4a"
          roughness={0.8}
          metalness={0.2}
        />
      </mesh>

      {/* Main building */}
      <mesh position={[0, 1.5, -5]}>
        <boxGeometry args={[20, 3, 25]} />
        <meshStandardMaterial 
          color="#e0e0e0"
          transparent={true}
          opacity={0.8}
        />
      </mesh>
      
      {/* Entrance */}
      <mesh position={[-7, 2, 10]}>
        <boxGeometry args={[6, 4, 3]} />
        <meshStandardMaterial 
          color="#d0d0d0"
          transparent={true}
          opacity={0.8}
        />
      </mesh>

      {/* Tower preview */}
      {towerPreviewPosition && (
        <TowerPreview 
          type={selectedTowerType} 
          position={towerPreviewPosition} 
        />
      )}
    </group>
  );
};

export default SchoolMap; 